import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { clientEnvironment, installAudioUnlock, track, usageCapture } from '@exante/gyro-react';
import { adminUsageSink } from './lib/usageCapture';
import { App } from './App';
import { applyPreference, readPreference } from './shell/theme';
// Order matters: the tokens, then the kit (the base rules, the components, the shell and the widget's colours); gyro.css
// last so the kit's `:root .gyro-panel` mapping is the more specific rule.
import './ui/tokens.css';
import './ui/kit.css';
import '@exante/gyro-react/gyro.css';

// Light or dark before the first paint: the person's pin from the rail, else the operating system's setting.
applyPreference(readPreference());

// Sentry error monitoring for the admin console. No-op unless VITE_SENTRY_DSN is set at build time, so it
// stays off until configured. `dataCollection` below is the SDK's own mapping of the old `sendDefaultPii: false`
// (removed in @sentry/react 11, whose new defaults collect everything): no inferred user/IP, PII-style
// header/cookie/query keys filtered, no HTTP bodies. Tracing is opt-in via env (default low).
// Frontend DSN — public by nature (it ships in the bundle). Overridable via VITE_SENTRY_DSN ('off' disables).
const DEFAULT_SENTRY_DSN = 'https://5a8e07e76b22ef54891313d5ed37affe@o4511469313130496.ingest.us.sentry.io/4511655208091648';
const dsnEnv = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const dnt = typeof navigator !== 'undefined' && (navigator.doNotTrack === '1' || (window as { doNotTrack?: string }).doNotTrack === '1');
const sentryDsn = dsnEnv === 'off' || dnt ? undefined : (dsnEnv || DEFAULT_SENTRY_DSN);
// Strip query strings from URLs — they can carry ids/emails/tokens in an admin console; the PII filtering
// only drops IP/cookies/headers, not breadcrumb/request URLs.
const stripQuery = (u?: string): string | undefined => (typeof u === 'string' ? u.split('?')[0] : u);
// Header/cookie/query-key snippets that @sentry/core 10.x denied under `sendDefaultPii: false`
// (utils/data-collection/defaultPiiToCollectionOptions). Kept verbatim so the 11.x upgrade changes nothing.
const PII_KEY_SNIPPETS = ['forwarded', '-ip', 'remote-', 'via', '-user'];
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0.1'),
    dataCollection: {
      userInfo: false,
      cookies: { deny: PII_KEY_SNIPPETS },
      httpHeaders: { request: { deny: PII_KEY_SNIPPETS }, response: { deny: PII_KEY_SNIPPETS } },
      httpBodies: [],
      urlQueryParams: { deny: PII_KEY_SNIPPETS },
      genAI: { inputs: false, outputs: false },
      databaseQueryData: false,
      // 10.x's ContextLines integration captured 7 lines; 11.x's default is 5.
      frameContextLines: 7,
    },
    beforeBreadcrumb(bc) {
      if (bc?.data && typeof (bc.data as { url?: string }).url === 'string') (bc.data as { url?: string }).url = stripQuery((bc.data as { url?: string }).url);
      return bc;
    },
    beforeSend(event) {
      if (event.request?.url) event.request.url = stripQuery(event.request.url);
      return event;
    },
  });
}

// Unlock audio on first interaction so Gyro's voice plays reliably.
installAudioUnlock();

// Gyro's own usage record (@exante/gyro-react usage/featureEvents.ts; the usage analytics contract): which console
// pages open and which features run, under the admin cookie session, batched to POST /v1/usage/events. No third-party
// vendor (the Aptabase client that used to be initialised here is gone). App.tsx reports the page on view and the
// signed-in account.
const usage = usageCapture();
usage.configure({ surface: 'admin', app_version: __GYRO_ADMIN_BUILD_SHA__ || 'dev', ...clientEnvironment() });
usage.installSink(adminUsageSink);
usage.installBrowserHooks();
track('admin_opened', { app: 'admin' });

// Register the service worker (enables PWA install / standalone window).
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => void navigator.serviceWorker.register('/sw.js').catch(() => undefined));
}

// Use the withErrorBoundary HOC rather than <Sentry.ErrorBoundary> as a JSX element: under React 19's
// stricter @types/react, the ErrorBoundary CLASS isn't accepted as a JSX component type, but the HOC
// returns a plain React.FC which is. Same runtime behavior (Sentry-reported boundary + fallback UI).
const RootApp = Sentry.withErrorBoundary(App, {
  fallback: (
    <div className="ui-gate">
      <div className="ui-gate-card" role="alert">
        <h1 className="ui-gate-title">Something went wrong</h1>
        <p className="ui-gate-text">Gyro Admin hit an error it could not recover from. Reload the page to start again.</p>
      </div>
    </div>
  ),
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <RootApp />
  </StrictMode>,
);
